import React, { useCallback } from "react"
import { Audio, Sequence, useVideoConfig } from "remotion"
import { joinSplitPieces, msToFrames } from "../../utils/timing"
import { duckGainAt, type SpeechRange } from "../../utils/duck"
import type { Track } from "../../schemas"

interface MusicTrackProps {
  track: Track
  /** Output-timeline speech ranges (ms) the music ducks under when duckUnderSpeech is on. */
  speechRanges?: SpeechRange[]
}

export const MusicTrack: React.FC<MusicTrackProps> = ({ track, speechRanges = [] }) => {
  const { fps } = useVideoConfig()

  const segments = joinSplitPieces(track.sections.flatMap(s => s.segments))

  return (
    <>
      {segments.map((segment) => {
        const inFrame = msToFrames(segment.timeline.in, fps)
        const duration = msToFrames(segment.timeline.out - segment.timeline.in, fps)
        const content = segment.content
        const src = content?.mediaInfo?.originUrl || content?.mediaInfo?.previewUrl
        if (!src) return null

        return (
          <Sequence key={segment.id} from={inFrame} durationInFrames={Math.max(duration, 1)}>
            <MusicSegmentAudio
              segment={segment}
              src={src}
              durationInFrames={duration}
              fps={fps}
              speechRanges={speechRanges}
            />
          </Sequence>
        )
      })}
    </>
  )
}

const MusicSegmentAudio: React.FC<{
  segment: Record<string, any>
  src: string
  durationInFrames: number
  fps: number
  speechRanges: SpeechRange[]
}> = ({ segment, src, durationInFrames, fps, speechRanges }) => {
  const content = segment.content
  const audioSettings = content.audioSettings ?? {}
  const baseVolume = (audioSettings.volume ?? 50) / 100
  const fadeInFrames = msToFrames(audioSettings.fadeInMs ?? 500, fps)
  const fadeOutFrames = msToFrames(audioSettings.fadeOutMs ?? 500, fps)
  const duck = audioSettings.duckUnderSpeech ?? true
  const duckLevel = audioSettings.duckLevel ?? 50
  const segmentStartMs = segment.timeline?.in ?? 0

  const volumeCallback = useCallback(
    (frame: number) => {
      let vol = baseVolume

      // Fade in
      if (frame < fadeInFrames && fadeInFrames > 0) {
        vol *= frame / fadeInFrames
      }

      // Fade out
      const fadeOutStart = durationInFrames - fadeOutFrames
      if (frame > fadeOutStart && fadeOutFrames > 0) {
        vol *= (durationInFrames - frame) / fadeOutFrames
      }

      if (duck && speechRanges.length) {
        vol *= duckGainAt(speechRanges, segmentStartMs + (frame / fps) * 1000, duckLevel)
      }

      return Math.max(0, Math.min(1, vol))
    },
    [baseVolume, fadeInFrames, fadeOutFrames, durationInFrames, duck, duckLevel, speechRanges, segmentStartMs, fps],
  )

  return (
    <Audio
      src={src}
      pauseWhenBuffering
      volume={volumeCallback}
      loop={audioSettings.loop ?? false}
      trimBefore={msToFrames(segment.trimStartMs ?? 0, fps) || undefined}
    />
  )
}
